import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { UpsertEstimationItemCostRequest } from '../../schema/data-contracts';
import type { MutationProps } from '../types';

const getEstimationItemCostsKey = 'estimation-item-costs';

export const useEstimationItemCostQueryActions = () => {
  const { auth } = useAuth();
  const {
    estimationItemCostController: {
      v1EstimationItemsCostsList,
      v1EstimationItemsCostsUpdate,
      v1EstimationsEstimationItemCostsList,
    },
  } = useApi();

  const queryClient = useQueryClient();

  const useGetEstimationItemCostsByEstimationItemId = (estimationItemId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && !!estimationItemId,
      queryKey: [getEstimationItemCostsKey, 'estimationItemId', estimationItemId],
      queryFn: async () => {
        const { data } = await v1EstimationItemsCostsList(estimationItemId!);
        return data;
      },
    });

  const useGetEstimationItemCostsByEstimationId = (estimationId: string | undefined) =>
    useQuery({
      enabled: Boolean(auth) && Boolean(estimationId),
      queryKey: [getEstimationItemCostsKey, 'estimationId', estimationId],
      queryFn: async () => {
        const { data } = await v1EstimationsEstimationItemCostsList(estimationId!);
        return data;
      },
    });

  const useUpdateEstimationItemCostsOfEstimationItem = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-item-costs-update'],
      mutationFn: async ({
        estimationItemId,
        estimationItemCosts,
      }: {
        estimationItemId: string;
        estimationItemCosts: UpsertEstimationItemCostRequest[];
      }) => {
        const { data } = await v1EstimationItemsCostsUpdate(estimationItemId, estimationItemCosts);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [getEstimationItemCostsKey],
        });
      },
    });

  return {
    useGetEstimationItemCostsByEstimationItemId,
    useGetEstimationItemCostsByEstimationId,
    useUpdateEstimationItemCostsOfEstimationItem,
  };
};
